import { FC, useEffect, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';

import { AttributeI, AttrInternalType } from '~src/interfaces/attributes.types';
import { fetchAttributes, FetchAttributesParams } from '~src/modules/attributes/actions/attributes.actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { AttributesRespI, AttrSortingType } from '~src/modules/attributes/interfaces';
import logger from '~src/services/logger/LlsLogger';
import { StoreI } from '~src/rootReducer';
import ListPageHeader from '~src/shared/headers/listPageHeader/ListPageHeader';
import { ListMargin, PageContentContainer } from '~src/styles/containers.sc';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import ListAddButton from '~src/shared/lists/listAddButton/ListAddButton';
import { routes } from '~src/routingConfig';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { MainContainerAnimation } from '~src/styles/animations';
import Pagination from '~src/shared/lists/pagination/Pagination';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import { PaginationI } from '~src/interfaces/pagination.interface';

import AttributesListItem from './AttributesListItem';
import { AttributesContainer, ItemsAmountRow } from './attributesList.sc';

interface StateProps {
  loadingAttributes: boolean;
  attributes: AttributeI[];
  pagination: PaginationI | null;
}

interface DispatchProps {
  fetchAttributes(params: FetchAttributesParams): ApiReduxResp<AttributesRespI>;
}

type Props = DispatchProps & StateProps & WithTranslation & RouteComponentProps<any>;

const AttributesList: FC<Props> = props => {
  const searchStrObj: { page?: string } = queryString.parse(props.location.search);
  const initialPage = Number(searchStrObj.page) || 1;

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    fetchAttributesCall(initialPage).then(() => {
      setInitialLoading(false);
    });
  }, []);

  const fetchAttributesCall = (page?: number) => {
    const params = {
      'filter.type': `${AttrInternalType.Datetime},${AttrInternalType.Decimal},${AttrInternalType.Option}`,
      include: 'options',
      page,
      sort: AttrSortingType.CreatedDesc,
    };
    return props
      .fetchAttributes(params)
      .then(({ error }) => {
        if (error) {
          throw new Error('Error fetching attributes list');
        }
      })
      .catch(error => {
        logger.info('Error fetching attributes list', {
          error,
          info: {
            componentStack: 'AttributesList.jsx',
          },
        });
      });
  };

  const goToAdd = () => {
    props.history.push(routes.company.attributesAdd);
  };

  return (
    <main>
      <ListPageHeader
        t={props.t}
        titleTxt="x.attributes.list.title"
        subTitleTxt="x.attributes.list.subtitle"
      />
      <PageContentContainer>
        <ListMargin>
          <ItemsAmountRow>
            {props.pagination && !props.loadingAttributes && (
              <ItemsAmount
                pagination={props.pagination}
                onePageKey="x.attributes.list.attributes_amount"
                multyPageKey="x.attributes.list.attributes_amount_from_to"
              />
            )}
          </ItemsAmountRow>
          <ListAddButton
            addFunc={goToAdd}
            buttonText={{
              key: 'x.attributes.add.button',
              txt: props.t('x.attributes.add.button'),
            }}
          />
          {props.loadingAttributes ? (
            <LoadingSpinner marginTop={60} />
          ) : (
            <MainContainerAnimation>
              <AttributesContainer>
                {props.attributes.map(attribute => (
                  <AttributesListItem
                    key={attribute.uuid}
                    t={props.t}
                    attribute={attribute}
                    link={`${props.match.url}/${attribute.uuid}`}
                  />
                ))}
              </AttributesContainer>
            </MainContainerAnimation>
          )}
          {props.pagination && !initialLoading && (
            <Pagination
              t={props.t}
              totalItems={props.pagination.total}
              pageSize={props.pagination.per_page}
              onChange={fetchAttributesCall}
              marginTop={60}
              history={props.history}
              location={props.location}
              defaultCurrPage={initialPage}
            />
          )}
          <TextBtnPageFooter
            t={props.t}
            infoKey="x.attributes.list.footer.title"
            btnKey="x.attributes.list.footer.button"
            linkUrl={routes.company.users}
          />
        </ListMargin>
      </PageContentContainer>
    </main>
  );
};

const mapStateToProps = ({ attributes }: StoreI): StateProps => ({
  loadingAttributes: attributes.loading,
  attributes: attributes.items,
  pagination: attributes.pagination,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchAttributes: params => dispatch(fetchAttributes(params)),
});

const AttributesListT = withTranslation()(AttributesList);
const AttributesListC = connect(mapStateToProps, mapDispatchToProps)(AttributesListT);

export default AttributesListC;
